// Copyright ©️ 2026 eVolve MEP, LLC

import { useRef } from 'react';

import type { EvolveApiRoute, EvolveApiResponse } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import { useSearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/useSearchableSelect';
import type { KeysOfType } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { weakOmit } from 'helpers/omit';

import type { TypeSafeRenderOption, WrappedMultiSelectProps } from '../types';
import { WrappedMultiSelect } from '../WrappedMultiSelect';
import type { SearchableMultiSelectorProps } from './types';

export type SearchableMultiSelectProps<
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
> = SearchableMultiSelectorProps<Route, T, TKey> &
  Omit<
    WrappedMultiSelectProps<string, object>,
    keyof SearchableMultiSelectorProps<Route, T, TKey> | 'data' | 'renderOption'
  > & {
    disableSearch?: boolean;
    getGroup?: (data: T) => string | undefined;
    nowrap?: boolean;
    preloadData?: boolean;
    renderOption?: TypeSafeRenderOption<string, { original: T }>;
  };

/**
 * Used for `MultiSelect`s whose "search" functionality
 * involves fetching data from the backend.
 */
export const SearchableMultiSelect = <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends EvolveApiResponse<'GET_PAGINATED', Route>['data'][number],
  TKey extends KeysOfType<T, string>,
>(
  props: SearchableMultiSelectProps<Route, T, TKey>,
) => {
  const {
    idKey,
    value: parentValue,
    onChange: parentOnChange,
    defaultValue: parentDefaultValue = [],
    onOptionSubmit,
    disableSearch = false,
    placeholder = 'Type to search...',
    nothingFoundMessage = 'No results found',
    ...multiSelectProps
  } = props;
  const ref = useRef<HTMLInputElement>(null);

  const [__value, __onChange] = useUncontrolled({
    value: parentValue,
    defaultValue: parentDefaultValue,
    onChange: parentOnChange,
  });

  const { options, latestDataLoaded, data, overrideKeys, overrideProps, fetchNextPage } = useSearchableSelect<
    Route,
    T,
    TKey
  >({ type: 'multi', __value, __onChange }, props);

  return (
    <WrappedMultiSelect
      ref={ref}
      placeholder={placeholder}
      nothingFoundMessage={disableSearch ? 'Start typing...' : !latestDataLoaded ? '...' : nothingFoundMessage}
      searchable
      hidePickedOptions
      noFilterOnSearch
      onClear={() => __onChange([])}
      clearSearchOnFocus={false}
      {...weakOmit(multiSelectProps, overrideKeys)}
      {...overrideProps}
      onFocus={(...e) => {
        overrideProps.onDropdownOpen();
        multiSelectProps.onFocus?.(...e);
      }}
      onBlur={(...e) => {
        overrideProps.onDropdownClose();
        multiSelectProps.onBlur?.(...e);
      }}
      onChange={(v) => {
        const ids = v as T[TKey][];
        const stillSelected = __value.filter((t) => ids.includes(t[idKey]));
        const additionalSelected = options.filter(
          (o) => ids.includes((o as T)[idKey]) && !stillSelected.some((t) => t[idKey] === o[idKey]),
        );
        const newSelectedOptions = [...stillSelected, ...additionalSelected];
        __onChange(newSelectedOptions);
      }}
      onOptionSubmit={(id) => {
        if (isNotNil(onOptionSubmit)) {
          const opt = __value.find((o) => o[idKey] === id) ?? options.find((o) => o[idKey] === id);
          if (isNotNil(opt)) {
            onOptionSubmit(opt);
          }
        }
      }}
      value={__value.map((t) => t[idKey] as string)}
      data={data}
      onScrollToEnd={fetchNextPage}
    />
  );
};
